import { useQuery } from '@tanstack/react-query';
import { api, type ApiSuccess } from '../lib/api';

interface SiteSettings {
  brandName?: string;
  brandTagline?: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor?: string;
}

/**
 * 🎨 Hook: يجيب إعدادات الهوية البصرية من الـ API
 *    cache لمدة طويلة لأن البراند ما يتغير كثير
 */
export function useBrandSettings() {
  const { data } = useQuery({
    queryKey: ['site-settings', 'public'],
    queryFn: () => api.get<ApiSuccess<SiteSettings>>('/api/settings/public').catch(() => ({ data: {} as SiteSettings })),
    staleTime: 5 * 60 * 1000,  // 5 دقائق
    retry: false,
  });
  return data?.data ?? {} as SiteSettings;
}

/**
 * 🏷️ مكوّن اللوقو الموحّد — استخدمه في كل الواجهات
 */
export function BrandLogo({
  size = 'md',
  showText = true,
  inverted = false,
  className = '',
}: {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  showText?: boolean;
  inverted?: boolean;
  className?: string;
}) {
  const settings = useBrandSettings();
  const name = settings.brandName ?? 'شركة صورة العقارية';
  const tagline = settings.brandTagline ?? 'منصة عقارية متكاملة';
  // 🎨 fallback للوقو الستاتيكي اللي حُفظ في public/
  const logoUrl = settings.logoUrl ?? '/logo.png';

  const sizes = {
    sm: { logo: 'w-8 h-8',  text: 'text-sm', sub: 'text-[10px]' },
    md: { logo: 'w-10 h-10', text: 'text-base', sub: 'text-xs' },
    lg: { logo: 'w-12 h-12', text: 'text-lg', sub: 'text-xs' },
    xl: { logo: 'w-16 h-16', text: 'text-xl', sub: 'text-sm' },
  };
  const s = sizes[size];

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      {logoUrl ? (
        <img
          src={logoUrl}
          alt={name}
          className={`${s.logo} object-contain`}
          onError={(e) => {
            // 🛡️ لو فشل تحميل اللوقو، نخفيه ونعرض الـ fallback النصي
            e.currentTarget.style.display = 'none';
            const sib = e.currentTarget.nextElementSibling as HTMLElement | null;
            if (sib) sib.style.display = 'flex';
          }}
        />
      ) : null}
      <div
        className={`${s.logo} rounded-xl bg-gold flex items-center justify-center font-black text-navy-darker shadow ${logoUrl ? 'hidden' : ''}`}
      >
        ص
      </div>
      {showText && (
        <div className={inverted ? 'text-white' : 'text-navy-darker'}>
          <div className={`font-black ${s.text} leading-tight`}>{name}</div>
          {tagline && <div className={`${s.sub} opacity-80 leading-tight`}>{tagline}</div>}
        </div>
      )}
    </div>
  );
}
